import React, { ReactElement } from 'react';
import { Link } from '@medely/web-components';
import {
  Box,
  Card,
  HStack,
  Icon,
  InfoBadge,
  InfoBadgeColor,
  Label,
  Text,
  VStack,
  palette,
} from '@medely/ui-kit';
import { TaskCardLayout } from '../taskCards/TaskCard';

interface NextStepsTemplateProps {
  color?: InfoBadgeColor;
  badge?: string;
  content?: string;
  title?: string;
  icon?: ReactElement;
  to?: string | { pathname: string; state?: any };
  external?: boolean;
  rightChevron?: boolean;
  onPress?: () => void;
}

export const NextStepsTemplate = ({
  color = 'default',
  badge,
  content,
  title,
  icon,
  to,
  external = false,
  rightChevron = true,
  onPress,
}: NextStepsTemplateProps): ReactElement => {
  const iconBgColor = (() => {
    switch (color) {
      case 'error':
        return palette.state.error['backing-1'];
      case 'info':
        return palette.state.info['backing-1'];
      case 'warning':
        return palette.state.warning['backing-1'];
      default:
        return 'primary.main';
    }
  })();

  const isClickable = !!(to || onPress);

  const cardContent = (
    <HStack flex={1} gap={2} alignItems="center">
      {icon && (
        <HStack
          justifyContent="center"
          alignItems="center"
          borderRadius="20px"
          width="40px"
          height="40px"
          bgcolor={iconBgColor}
        >
          {icon}
        </HStack>
      )}
      <VStack gap={0.5} flex={1} justifyContent="center">
        {!!title && <Label size="m">{title}</Label>}
        {!!content && <Text size="m">{content}</Text>}
        {!!badge && (
          <Box mt={content ? 1 : 0}>
            <InfoBadge label={badge} color={color} />
          </Box>
        )}
      </VStack>
      {isClickable && rightChevron && (
        <Box alignSelf="center">
          <Icon name="chevron-right" boxSize="24px" iconSize="16px" />
        </Box>
      )}
    </HStack>
  );

  return (
    <TaskCardLayout>
      <Card elevation={1} testId="next-steps-template">
        {isClickable ? (
          <Link
            target={external ? '_blank' : undefined}
            href={external ? to?.toString() : undefined}
            to={external ? undefined : to}
            onClick={onPress}
            color={palette.text.primary}
            sx={{ '&:hover': { textDecoration: 'none' } }}
          >
            {cardContent}
          </Link>
        ) : (
          cardContent
        )}
      </Card>
    </TaskCardLayout>
  );
};

export default NextStepsTemplate;
